import { useTranslation } from 'react-i18next';
import { Cookie } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@/hooks/useCookieConsent';

/**
 * Cookie Banner Component
 *
 * Displays a fixed bottom banner on first visit to comply with LSSI Art. 22.2
 * and GDPR requirements for cookie consent.
 *
 * Features:
 * - Shows only on first visit (no previous consent)
 * - 2 main options: Accept all or Only essentials
 * - Link to full Cookie Policy
 * - Responsive design (mobile, tablet, desktop)
 * - Accessible (WCAG 2.1 AA compliant)
 * - i18n support (8 languages)
 *
 * UI Design:
 * - Fixed bottom position (z-index 50)
 * - Semi-transparent backdrop
 * - Card with cookie icon, title, description, and buttons
 *
 * Integration:
 * - Automatically hides after user makes a choice
 * - Persists consent in localStorage via useCookieConsent hook
 * - Integrates with Umami Analytics (enables/disables tracking)
 *
 * @example
 * ```tsx
 * import { CookieBanner } from '@/components/cookie-banner';
 *
 * function App() {
 *   return (
 *     <>
 *       <YourAppContent />
 *       <CookieBanner />
 *     </>
 *   );
 * }
 * ```
 */
export const CookieBanner = () => {
  const { t } = useTranslation('cookie-policy');
  const { showBanner, saveConsent } = useCookieConsent();

  // Don't render if user already gave consent
  if (!showBanner) {
    return null;
  }

  /**
   * Handle "Accept all" button click
   * Saves consent with analytics enabled
   */
  const handleAcceptAll = () => {
    saveConsent(true); // Accept analytics cookies
  };

  /**
   * Handle "Only essentials" button click
   * Saves consent with analytics disabled
   */
  const handleRejectNonEssential = () => {
    saveConsent(false); // Only technical cookies
  };

  /**
   * Open full cookie policy in new tab
   */
  const handleLearnMore = () => {
    window.open('/cookie-policy', '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {/* Backdrop: semi-transparent overlay */}
      <div
        className='fixed inset-0 bg-black/20 backdrop-blur-sm z-40'
        aria-hidden='true'
      />

      {/* Banner: fixed bottom with card */}
      <div
        className='fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6'
        role='dialog'
        aria-label={t('banner.title')}
        aria-live='polite'
      >
        <Card className='max-w-4xl mx-auto shadow-2xl border-2 border-primary/20'>
          <CardContent className='p-4 sm:p-6'>
            <div className='flex flex-col gap-4'>
              {/* Header: Icon + Title */}
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0'>
                  <Cookie className='h-6 w-6 text-primary' aria-hidden='true' />
                </div>
                <div className='flex-1'>
                  <h2 className='text-lg sm:text-xl font-semibold text-foreground'>
                    {t('banner.title')}
                  </h2>
                  <p className='text-sm sm:text-base text-muted-foreground mt-1'>
                    {t('banner.description')}
                  </p>
                </div>
              </div>

              {/* Buttons: Following app style guide (w-full h-12 text-base) */}
              <div className='flex flex-col sm:flex-row gap-2 sm:gap-3'>
                {/* Primary: Accept all */}
                <Button
                  onClick={handleAcceptAll}
                  className='w-full h-12 text-base font-medium'
                  variant='default'
                >
                  {t('banner.acceptAll')}
                </Button>

                {/* Secondary: Only essentials */}
                <Button
                  onClick={handleRejectNonEssential}
                  className='w-full h-12 text-base font-medium'
                  variant='outline'
                >
                  {t('banner.rejectNonEssential')}
                </Button>

                {/* Tertiary: Learn more */}
                <Button
                  onClick={handleLearnMore}
                  className='w-full h-12 text-base font-medium'
                  variant='ghost'
                >
                  {t('banner.learnMore')} →
                </Button>
              </div>

              {/* Optional: Small legal text */}
              <p className='text-xs text-muted-foreground/70 text-center sm:text-left'>
                {t('whatAreCookies.legalBasis')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
